import { useQuery } from '@tanstack/react-query'
import type {
  FleetAutoscalerViewReadable,
  FleetCellView,
  FleetProviderView,
  FleetSignalView,
  FleetUnitView,
  ListFleetSignalsData,
} from '@/shared/api/__generated__'
import {
  fleetAutoscalerOptions,
  fleetOverviewOptions,
  getFleetCellOptions,
  getFleetUnitOptions,
  listFleetProvidersOptions,
  listFleetSignalsOptions,
  listFleetUnitsOptions,
} from './queries'

export interface FleetQueryState {
  isPending: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

interface OverviewFilter {
  // Терминальные записи (удалённые юниты, разобранные ячейки) бэк отдаёт
  // только по ?include=all — их включает тумблер экрана.
  includeAll?: boolean
}

export function useFleetOverview(filter: OverviewFilter = {}): FleetQueryState & {
  units: FleetUnitView[]
  cells: FleetCellView[]
  providers: FleetProviderView[]
} {
  const overview = useQuery(
    fleetOverviewOptions(filter.includeAll ? { query: { include: 'all' } } : {}),
  )
  return {
    units: overview.data?.units ?? [],
    cells: overview.data?.cells ?? [],
    providers: overview.data?.providers ?? [],
    isPending: overview.isPending,
    isError: overview.isError,
    error: overview.error,
    refetch: () => void overview.refetch(),
  }
}

// Дриллдаун юнита — точечная ручка; провайдера и ячейку хук добирает сам,
// чтобы паспорт юнита оставался одним вызовом со стороны виджета.
export function useFleetUnit(unitId: string): FleetQueryState & {
  unit: FleetUnitView | undefined
  provider: FleetProviderView | undefined
  cell: FleetCellView | undefined
} {
  const query = useQuery(getFleetUnitOptions({ path: { id: unitId } }))
  const providers = useQuery(listFleetProvidersOptions())
  const unit = query.data?.data
  const cellId = unit?.cell_id ?? ''
  const cell = useQuery({
    ...getFleetCellOptions({ path: { id: cellId } }),
    enabled: cellId !== '',
  })

  return {
    unit,
    provider: (providers.data?.data ?? []).find((p) => p.id === unit?.provider_account_id),
    cell: cell.data?.data,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useFleetCell(cellId: string): FleetQueryState & {
  cell: FleetCellView | undefined
  members: FleetUnitView[]
} {
  const query = useQuery(getFleetCellOptions({ path: { id: cellId } }))
  const cell = query.data?.data
  // Состав ячейки может держать уже удалённый юнит — берём список с include=all,
  // иначе паспорт ячейки терял бы часть строя.
  const units = useQuery(listFleetUnitsOptions({ query: { include: 'all' } }))
  const memberIds = cell ? [cell.c_unit_id, ...(cell.l_unit_ids ?? [])] : []

  return {
    cell,
    members: memberIds
      .map((id) => (units.data?.data ?? []).find((u) => u.id === id))
      .filter((u): u is FleetUnitView => u != null),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function providerName(providers: FleetProviderView[], id: string): string {
  return providers.find((p) => p.id === id)?.name ?? id
}

type SignalQuery = NonNullable<ListFleetSignalsData['query']>

/** Виды и типы субъектов — перечисления бэка, а не свободные строки. */
export type SignalKindFilter = NonNullable<SignalQuery['kind']>
export type SignalSubjectFilter = NonNullable<SignalQuery['subject_type']>

export interface SignalFilter {
  /** `undefined` не сужает: активные и снятые приезжают вместе. */
  active?: boolean
  kind?: SignalKindFilter
  subjectType?: SignalSubjectFilter
  subjectId?: string
  /**
   * `false` — не спрашивать вовсе. Нужен там, где субъект ещё не известен:
   * фильтр по пустому идентификатору бэк не сужает, и доска вернула бы чужие
   * сигналы вместо «пока нечего показывать».
   */
  enabled?: boolean
}

/**
 * Доска «требует внимания». Опрос частый: сигнал — повод вмешаться, и узнавать
 * о нём через F5 оператор не должен (ленты событий наружу нет).
 */
export function useFleetSignals(
  filter: SignalFilter = {},
): FleetQueryState & { signals: FleetSignalView[] } {
  const query = useQuery({
    ...listFleetSignalsOptions({
      query: {
        ...(filter.active === undefined ? {} : { active: filter.active ? 'true' : 'false' }),
        ...(filter.kind ? { kind: filter.kind } : {}),
        ...(filter.subjectType ? { subject_type: filter.subjectType } : {}),
        ...(filter.subjectId ? { subject_id: filter.subjectId } : {}),
      },
    }),
    enabled: filter.enabled ?? true,
    refetchInterval: 20_000,
  })
  return {
    signals: query.data?.data ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

/**
 * Снимок последнего прогона автоскейлера. `never_ran` приезжает телом, а не
 * 404: «тик ещё не отработал» — состояние системы, а не отсутствие ручки.
 */
export function useAutoscalerRun(): FleetQueryState & {
  run: FleetAutoscalerViewReadable | undefined
} {
  const query = useQuery({ ...fleetAutoscalerOptions(), refetchInterval: 20_000 })
  return {
    run: query.data,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}
